/**
 * ColumnChart — the mockup's one column engine, in three modes (spec-business §6):
 *   stacked   C2 · C8 · C9  — composition over time, total label on the cap;
 *   diverging C4            — "נכנס" above zero, "יצא" below, plus a net line;
 *   grouped   C7            — two bars per period with a direct "לתשלום" label above.
 *
 * One y-axis, ≤ 24–30px columns with 4px-rounded data ends, 2px gaps between stacked segments,
 * a mandatory legend for ≥ 2 series, a partial (running) column at 55% opacity, per-column hit
 * areas that are focusable and share the hover tooltip, and a table twin.
 */

import { ChartBlock, type LegendItem, type TableTwin } from './ChartBlock.tsx';
import { ChartTip, useTooltip, type TipContent } from './Tooltip.tsx';
import { bottomRoundedRect, niceTicks, topRoundedRect } from './chart-utils.ts';
import { useChartWidth } from './useChartWidth.ts';
import { fmtK } from '../format.ts';

export interface ColumnSeries {
  key: string;
  label: string;
  /** agorot, one per label. */
  values: number[];
  /** 1..6 — the fixed categorical order of `--series-N`. */
  series: number;
}

export interface ColumnChartProps {
  labels: string[];
  series: ColumnSeries[];
  mode?: 'stacked' | 'diverging' | 'grouped';
  /** A net line drawn over the columns (C4). */
  line?: { label: string; values: number[]; labelIndex?: number };
  /** Direct labels above each group (C7 "לתשלום"). */
  topLabels?: string[];
  /** Index of the running, partial column (dimmed + "עד כה" in the tooltip). */
  partialIndex?: number;
  height?: number;
  maxBar?: number;
  /** Verbal summary for the figure's accessible name. */
  summary: string;
  table: TableTwin;
  tip: (index: number) => TipContent;
  legendTwoCol?: boolean;
  footer?: React.ReactNode;
  /** Hide the total label above stacked columns. */
  totalLabels?: boolean;
}

export function ColumnChart({
  labels,
  series,
  mode = 'stacked',
  line,
  topLabels,
  partialIndex = -1,
  height = 220,
  maxBar = 24,
  summary,
  table,
  tip,
  legendTwoCol,
  footer,
  totalLabels = true,
}: ColumnChartProps) {
  const [ref, width] = useChartWidth(720);
  const tooltip = useTooltip();

  const W = Math.max(width, 240);
  const mL = 44;
  const mR = 12;
  const mT = 26;
  const mB = 24;
  const pw = W - mL - mR;
  const ph = height - mT - mB;
  const n = labels.length || 1;
  const slot = pw / n;
  const barWidth = Math.min(maxBar, slot * 0.62);

  const first = series[0];
  const second = series[1];
  let vmax = 0;
  let vmin = 0;
  labels.forEach((_l, i) => {
    if (mode === 'diverging') {
      vmax = Math.max(vmax, first?.values[i] ?? 0);
      vmin = Math.min(vmin, -(second?.values[i] ?? 0));
    } else if (mode === 'grouped') {
      for (const s of series) vmax = Math.max(vmax, s.values[i] ?? 0);
    } else {
      vmax = Math.max(vmax, series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
    }
  });
  if (line) vmax = Math.max(vmax, ...line.values);

  const ticks =
    mode === 'diverging'
      ? (() => {
          const m = Math.max(vmax, -vmin) * 1.12;
          return niceTicks(-m, m, 6);
        })()
      : niceTicks(0, vmax * (topLabels ? 1.15 : 1.08), 4);

  const y = (value: number) => mT + ph - ((value - ticks.min) / (ticks.max - ticks.min || 1)) * ph;
  const x = (index: number) => mL + slot * index + slot / 2;

  const legend: LegendItem[] = series.map((s) => ({ label: s.label, color: `var(--series-${s.series})` }));
  if (line) legend.push({ label: line.label, color: 'var(--text-1)', kind: 'line' });

  return (
    <div ref={ref}>
      <ChartBlock summary={summary} legend={legend.length > 1 ? legend : undefined} legendTwoCol={legendTwoCol} table={table} footer={footer}>
        <div className="plot">
          <svg viewBox={`0 0 ${W} ${height}`} width={W} height={height}>
            <g className="grid">
              {ticks.ticks.map((t) => (
                <line key={t} x1={mL} x2={W - mR} y1={y(t)} y2={y(t)} className={t === 0 ? 'zero' : undefined} />
              ))}
            </g>
            <g className="axis">
              {ticks.ticks.map((t) => (
                <text key={t} x={mL - 6} y={y(t) + 3.5} textAnchor="end" className="lbl2">
                  {fmtK(t)}
                </text>
              ))}
            </g>

            {labels.map((label, i) => {
              const dim = partialIndex === i ? 0.55 : 1;
              const cx = x(i);
              const nodes: React.ReactNode[] = [];

              if (mode === 'diverging') {
                const inValue = first?.values[i] ?? 0;
                const outValue = second?.values[i] ?? 0;
                nodes.push(
                  <path key="in" d={topRoundedRect(cx - barWidth / 2, y(inValue), barWidth, y(0) - y(inValue) - 1, 4)} fill={`var(--series-${first?.series ?? 1})`} opacity={dim} />,
                  <path key="out" d={bottomRoundedRect(cx - barWidth / 2, y(0) + 1, barWidth, y(-outValue) - y(0) - 1, 4)} fill={`var(--series-${second?.series ?? 2})`} opacity={dim} />,
                );
              } else if (mode === 'grouped') {
                const gw = barWidth * 0.8;
                const total = series.length * gw + (series.length - 1) * 2;
                let sx = cx - total / 2;
                for (const s of series) {
                  const value = s.values[i] ?? 0;
                  nodes.push(<path key={s.key} d={topRoundedRect(sx, y(value), gw, y(0) - y(value), 3)} fill={`var(--series-${s.series})`} opacity={dim} />);
                  sx += gw + 2;
                }
                if (topLabels?.[i]) {
                  const peak = Math.max(...series.map((s) => s.values[i] ?? 0));
                  nodes.push(
                    <text key="top" x={cx} y={y(peak) - 6} textAnchor="middle" className="lbl" style={{ fontSize: '11px' }}>
                      {topLabels[i]}
                    </text>,
                  );
                }
              } else {
                let acc = 0;
                const total = series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0);
                const lastIndex = series.reduce((last, s, k) => ((s.values[i] ?? 0) > 0 ? k : last), -1);
                series.forEach((s, k) => {
                  const value = s.values[i] ?? 0;
                  if (value <= 0) return;
                  const y0 = y(acc + value);
                  const h = y(acc) - y(acc + value) - (k === lastIndex ? 0 : 2);
                  nodes.push(
                    k === lastIndex ? (
                      <path key={s.key} d={topRoundedRect(cx - barWidth / 2, y0, barWidth, h, 4)} fill={`var(--series-${s.series})`} opacity={dim} />
                    ) : (
                      <rect key={s.key} x={cx - barWidth / 2} y={y0} width={barWidth} height={Math.max(0, h)} fill={`var(--series-${s.series})`} opacity={dim} />
                    ),
                  );
                  acc += value;
                });
                if (totalLabels && total > 0) {
                  nodes.push(
                    <text key="total" x={cx} y={y(total) - 5} textAnchor="middle" className="lbl" style={{ fontSize: '11px' }}>
                      {fmtK(total)}
                    </text>,
                  );
                }
              }

              return (
                <g key={`${label}-${i}`}>
                  {nodes}
                  <text x={cx} y={height - 7} textAnchor="middle" className="lbl3">
                    {label}
                  </text>
                </g>
              );
            })}

            {line ? (
              <g>
                <path
                  d={line.values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join('')}
                  fill="none"
                  stroke="var(--text-1)"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                {line.values.map((v, i) => (
                  <circle key={i} cx={x(i)} cy={y(v)} r="4" fill="var(--text-1)" className="ring" opacity={partialIndex === i ? 0.55 : 1} />
                ))}
                {line.labelIndex != null && line.values[line.labelIndex] != null ? (
                  <text x={x(line.labelIndex)} y={y(line.values[line.labelIndex]!) - 9} textAnchor="middle" className="lbl" style={{ fontSize: '11px' }}>
                    {fmtK(line.values[line.labelIndex]!)}
                  </text>
                ) : null}
              </g>
            ) : null}

            {labels.map((label, i) => {
              const content = tip(i);
              const handlers = tooltip.bind(content, { x: x(i), y: mT });
              return (
                <rect
                  key={`hit-${label}-${i}`}
                  className="bar-hit"
                  x={mL + slot * i}
                  y={mT}
                  width={slot}
                  height={ph}
                  tabIndex={0}
                  role="img"
                  aria-label={`${content.title}: ${content.rows.map((r) => `${r.label} ${r.value}`).join(', ')}`}
                  {...handlers}
                />
              );
            })}
          </svg>
        </div>
        <ChartTip state={tooltip.state} width={W} />
      </ChartBlock>
    </div>
  );
}
